import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Divider, Typography, useMediaQuery } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import { Fragment, useMemo } from 'react';
import { Permission, StyleObj } from '../../@types';
import { CasinoConfig } from '../../@types/api';
import { Config, NAVIGATION_ITEMS_LIST, NavigationObjectType } from '../../config';
import usePermissions from '../../hooks/usePermissions';
import NavigationItem from './NavigationItem';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useMarketplace } from '../../hooks/useMarketplace';

export const drawerWidth = 240;

type NavigationProps = {
  open: boolean;
  handleDrawerClose: () => void;
  handleDrawerOpen: () => void;
};

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledDrawer = styled(MuiDrawer)(({ theme, open, variant }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(variant === 'permanent' && {
    ...(open && {
      ...styles.openedMixin?.(theme),
      '& .MuiDrawer-paper': styles.openedMixin?.(theme),
    }),
    ...(!open && {
      ...styles.closedMixin?.(theme),
      '& .MuiDrawer-paper': styles.closedMixin?.(theme),
    }),
  }),
  ...(variant === 'temporary' && {
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      backgroundColor: theme.palette.background.light,
      borderRight: 'none',
    },
  }),
}));

const styles: StyleObj & {
  openedMixin?: (theme: Theme) => CSSObject;
  closedMixin?: (theme: Theme) => CSSObject;
} = {
  openedMixin: (theme) => ({
    backgroundColor: theme.palette.background.light,
    borderRight: 'none',
    width: drawerWidth,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  }),
  closedMixin: (theme): CSSObject => ({
    backgroundColor: theme.palette.background.light,
    borderRight: 'none',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(9)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(10)} + 1px)`,
    },
  }),

  navItem: { color: 'primary.main', px: 3, pb: 1, pt: 1 },
  list: { py: 0 },
  divider: { backgroundColor: 'primary.main', my: 1, mx: 3 },
  rightIcon: { mr: 1.5 },
};

const filterSectionByPermission = (
  items: NavigationObjectType[],
  hasPermission: (permission: Permission) => boolean,
  isStandaloneCasino?: boolean,
  marketplaceFeatures?: Record<string, boolean>,
  marketplaceSystems?: Record<string, boolean>,
  casinoConfig?: CasinoConfig,
  casinoProductEnabled?: boolean
) => {
  return items.filter((item) => {
    if (isStandaloneCasino && item.requireSportsbook) {
      return false;
    }

    if (
      isStandaloneCasino &&
      item.standaloneCasinoSystemFlag &&
      !marketplaceSystems?.[item.standaloneCasinoSystemFlag]
    ) {
      return false;
    }

    // A mixed marketplace keeps shared navigation such as Jackpot for its Sportsbook product.
    if (
      isStandaloneCasino &&
      item.standaloneCasinoConfigFlag &&
      (!casinoProductEnabled || !casinoConfig?.[item.standaloneCasinoConfigFlag])
    ) {
      return false;
    }

    if (item.featureFlag && marketplaceFeatures && !marketplaceFeatures[item.featureFlag]) {
      return false;
    }

    if (item.marketplaceSystemFlag && marketplaceSystems && !marketplaceSystems[item.marketplaceSystemFlag]) {
      return false;
    }

    if (!!item.children?.length) {
      item.children = filterSectionByPermission(
        item.children,
        hasPermission,
        isStandaloneCasino,
        marketplaceFeatures,
        marketplaceSystems,
        casinoConfig,
        casinoProductEnabled
      );
    }

    return !item.permission || hasPermission(item.permission);
  });
};

export const filterConfigByPermission = (
  config: Config,
  hasPermission: (permission: Permission) => boolean,
  isStandaloneCasino?: boolean,
  marketplaceFeatures?: Record<string, boolean>,
  marketplaceSystems?: Record<string, boolean>,
  casinoConfig?: CasinoConfig,
  casinoProductEnabled?: boolean
): Config => {
  const filteredConfig = {} as Config;

  for (const key in config) {
    const section = config[key as keyof Config];
    const filteredItems = filterSectionByPermission(
      section,
      hasPermission,
      isStandaloneCasino,
      marketplaceFeatures,
      marketplaceSystems,
      casinoConfig,
      casinoProductEnabled
    );

    if (filteredItems.length > 0) {
      filteredConfig[key as keyof Config] = filteredItems;
    }
  }

  return filteredConfig;
};

const DrawerNavigation = ({ open, handleDrawerClose, handleDrawerOpen }: NavigationProps) => {
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const hideBonus = useFeatureIsOn('hide-bonus-page');

  const { isStandaloneCasino, config, isSuccess } = useMarketplace();

  const filteredConfig = useMemo(() => {
    if (isSuccess) {
      const marketplaceFeatures = config?.marketplaceConfig?.features || {};
      const marketplaceSystems = config?.marketplaceConfig?.systems || {};

      return filterConfigByPermission(
        NAVIGATION_ITEMS_LIST,
        hasPermission,
        isStandaloneCasino,
        marketplaceFeatures,
        marketplaceSystems,
        config?.casinoConfig,
        config?.marketplaceConfig?.products?.casino === true
      );
    }

    return {} as Config;
  }, [
    hasPermission,
    isStandaloneCasino,
    config?.casinoConfig,
    config?.marketplaceConfig?.features,
    config?.marketplaceConfig?.products?.casino,
    config?.marketplaceConfig?.systems,
    isSuccess,
  ]);
  const renderNavigationSection = (sectionName: string, items: NavigationObjectType[], isLastSection: boolean) => {
    return (
      <Fragment key={sectionName}>
        {open && (
          <Typography variant='subtitle1' sx={styles.navItem}>
            {sectionName}
          </Typography>
        )}
        <List sx={styles.list}>
          {items.map((item) => (
            <NavigationItem
              open={open}
              item={item}
              key={item.name}
              isSmDown={isSmDown}
              handleDrawerClose={handleDrawerClose}
            />
          ))}
        </List>
        {!isLastSection && <Divider sx={styles.divider} />}
      </Fragment>
    );
  };

  const renderNavigationDrawerContent = () => {
    const sectionKeys = Object.keys(filteredConfig);

    return sectionKeys.map((key, index) => {
      const isLastSection = index === sectionKeys.length - 1;
      const sectionItems = filteredConfig[key as keyof Config];
      const filteredItems = hideBonus ? sectionItems.filter((item) => item.name !== 'Bonus') : sectionItems;

      return renderNavigationSection(key, filteredItems, isLastSection);
    });
  };

  return (
    <StyledDrawer
      variant={isSmDown ? 'temporary' : 'permanent'}
      open={open}
      onClose={isSmDown ? handleDrawerClose : undefined}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <DrawerHeader>
        <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen} color='primary' disableRipple>
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon sx={styles.rightIcon} />}
        </IconButton>
      </DrawerHeader>
      {renderNavigationDrawerContent()}
    </StyledDrawer>
  );
};

export default DrawerNavigation;
